import { fmt, interpretSpeech, spokenText } from "@mathtrain/engine";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Keypad, type Key } from "../components/Keypad";
import { api, ApiError } from "../lib/api";
import { getVoicePrefs, listen, speak, stopSpeaking, type Listener } from "../lib/voice";
import type { AnswerResult, PlayRequest, PublicProblem, Summary } from "../lib/types";

type Phase = "loading" | "answer" | "sending" | "feedback" | "summary" | "error";
const TAGS: Record<string, string> = { drill: "Practice", retry: "Try this one again", review: "Review from an earlier session", daily: "Daily challenge", challenge: "Challenge" };

export function Play() {
  const nav = useNavigate();
  const req = useLocation().state as PlayRequest | null;
  const [sid, setSid] = useState<string | null>(null);
  const [problem, setProblem] = useState<PublicProblem | null>(null);
  const [input, setInput] = useState("");
  const [phase, setPhase] = useState<Phase>("loading");
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [err, setErr] = useState("");
  const [, tick] = useState(0);
  const shownAt = useRef(performance.now());
  const pending = useRef<{ next: PublicProblem | null; summary: Summary | null }>({ next: null, summary: null });
  const auto = useRef<number>(0);
  const started = useRef(false);
  const voice = useRef(getVoicePrefs());
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState("");
  const [micMsg, setMicMsg] = useState("");
  const listener = useRef<Listener | null>(null);
  const sendTimer = useRef<number>(0);
  const quiet = useRef(0);

  const show = useCallback((p: PublicProblem) => {
    clearTimeout(sendTimer.current); setHeard("");
    setProblem(p); setInput(""); setResult(null); setHint(null); setErr(""); setPhase("answer");
    shownAt.current = performance.now();
  }, []);

  useEffect(() => {
    if (!req) { nav("/", { replace: true }); return; }
    if (started.current) return;
    started.current = true;
    api<{ sessionId: string; problem: PublicProblem }>("/play/start", { body: { ...req, audio: voice.current.speak || voice.current.listen } })
      .then((r) => { setSid(r.sessionId); show(r.problem); })
      .catch((e) => { setErr(e.message); setPhase("error"); });
  }, [req, nav, show]);

  useEffect(() => {
    if (phase !== "answer") return;
    const id = window.setInterval(() => tick((x) => x + 1), 100);
    return () => clearInterval(id);
  }, [phase]);

  const advance = useCallback(() => {
    clearTimeout(auto.current);
    const { next, summary: s } = pending.current;
    if (s) { setSummary(s); setPhase("summary"); }
    else if (next) show(next);
    else nav("/");
  }, [show, nav]);

  const submit = async (skip = false, spoken?: string) => {
    if (phase !== "answer" || !sid || !problem) return;
    const value = spoken ?? input;
    if (!skip && !/\d/.test(value)) return;
    clearTimeout(sendTimer.current); listener.current?.stop(); setListening(false); setHeard("");
    setPhase("sending");
    try {
      const r = await api<{ result: AnswerResult; next: PublicProblem | null; summary: Summary | null }>(`/play/${sid}/answer`,
        { body: { problemId: problem.id, answer: skip ? null : value, skip } });
      pending.current = { next: r.next, summary: r.summary };
      setResult(r.result); setPhase("feedback");
      if (r.result.correct && !voice.current.speak) auto.current = window.setTimeout(advance, r.result.speed === "fast" || r.result.speed === "on-target" ? 700 : 1000);
    } catch (e) {
      setPhase("answer");
      setErr(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };

  const askHint = async () => {
    if (phase !== "answer" || hint || !sid || !problem) return;
    try { setHint((await api<{ hint: string }>(`/play/${sid}/hint`, { body: { problemId: problem.id } })).hint); }
    catch (e: any) { setErr(e.message); }
  };

  const quit = async () => {
    clearTimeout(auto.current); stopVoice();
    if (!sid || phase === "summary") return nav("/");
    try {
      const r = await api<{ summary: Summary | null }>(`/play/${sid}/end`, { method: "POST" });
      if (r.summary) { setSummary(r.summary); setPhase("summary"); } else nav("/");
    } catch { nav("/"); }
  };

  const onKey = (k: Key) => {
    clearTimeout(sendTimer.current); setHeard("");
    if (phase === "feedback") { if (k === "enter") advance(); return; }
    if (phase !== "answer") return;
    if (k === "enter") return void submit();
    if (k === "skip") return void submit(true);
    if (k === "hint") return void askHint();
    setErr("");
    setInput((v) => {
      if (k === "del") return v.slice(0, -1);
      if (k === "neg") return v.startsWith("-") ? v.slice(1) : "-" + v;
      if (k === ".") return v.includes(".") ? v : (v.replace("-", "") === "" ? v + "0." : v + ".");
      return v.replace(/[-.]/g, "").length < 12 ? v + k : v;
    });
  };
  // ---- Voice: read the problem aloud, then listen for the answer. ----
  const stopVoice = () => { clearTimeout(sendTimer.current); listener.current?.stop(); listener.current = null; stopSpeaking(); setListening(false); };
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const hintRef = useRef(askHint);
  hintRef.current = askHint;
  const advanceRef = useRef(advance);
  advanceRef.current = advance;
  const sayRef = useRef<(text: string) => void>(() => {});
  sayRef.current = (text) => { if (voice.current.speak) speak(text); };

  const startListening = useCallback(() => {
    if (!voice.current.listen) return;
    listener.current?.stop();
    setMicMsg(""); setListening(true);
    listener.current = listen((alts) => {
      let h = null;
      for (const a of alts) { h = interpretSpeech(a); if (h) { setHeard(a); break; } }
      if (!h) { setMicMsg("Didn't catch a number."); return; }
      quiet.current = 0;
      if (h.kind === "command") {
        if (h.cmd === "repeat") return sayAgain();
        if (h.cmd === "hint") return void hintRef.current();
        if (h.cmd === "skip") return void submitRef.current(true);
        if (h.cmd === "next") return;
        clearTimeout(sendTimer.current); setInput(""); setHeard(""); return;   // cancel
      }
      setInput(h.value);
      clearTimeout(sendTimer.current);
      sendTimer.current = window.setTimeout(() => void submitRef.current(false, h.value), 1500);
    }, (why) => {
      setListening(false);
      if (why === "denied") { setMicMsg("Microphone is blocked. Allow it in your browser settings, or turn off voice answers on the Me tab."); return; }
      if (why === "error") { setMicMsg("Voice input isn't working here. Use the keypad."); return; }
      // Keep listening through short silences, then wait for a tap so the mic isn't open forever.
      if (++quiet.current < 4) setTimeout(() => { if (phaseRef.current === "answer") startListeningRef.current(); }, 250);
      else setMicMsg("Tap the mic to keep listening.");
    });
  }, []);
  const startListeningRef = useRef(startListening);
  startListeningRef.current = startListening;
  const problemRef = useRef(problem);
  problemRef.current = problem;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  function sayAgain() {
    const p = problemRef.current;
    if (!p) return;
    listener.current?.stop(); setListening(false);
    speak(spokenText(p.q, p.note), () => { if (phaseRef.current === "answer") startListeningRef.current(); });
  }

  // New problem: say it, then listen.
  const pid = problem?.id;
  useEffect(() => {
    if (phase !== "answer" || !problem) return;
    quiet.current = 0;
    const { speak: sp, listen: li } = voice.current;
    if (sp) { const c = speak(spokenText(problem.q, problem.note), () => { if (li) startListeningRef.current(); }); return () => { c(); listener.current?.stop(); }; }
    if (li) { startListeningRef.current(); return () => listener.current?.stop(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, phase === "answer"]);

  // Feedback: say how it went, then move on hands-free when the player is answering by voice.
  useEffect(() => {
    if (phase !== "feedback" || !result || !voice.current.speak) return;
    const r = result;
    const line = r.right ? (r.speed === "fast" || r.speed === "on-target" ? "Correct." : "Correct, but slow.") : `${r.skipped ? "Skipped." : "Not quite."} The answer is ${fmt(r.answer)}.`;
    const c = speak(line, () => {
      const wait = r.right ? 250 : voice.current.listen ? 2500 : 0;
      if (wait) auto.current = window.setTimeout(() => advanceRef.current(), wait);
    });
    return () => c();
  }, [phase, result]);

  // A hint asked for by voice or tap is read aloud too; the mic pauses while it plays.
  useEffect(() => {
    if (!hint || !voice.current.speak) return;
    listener.current?.stop(); setListening(false);
    const c = speak(spokenText(hint.replace(/\n/g, ". ")), () => { if (phaseRef.current === "answer") startListeningRef.current(); });
    return () => c();
  }, [hint]);

  useEffect(() => () => { clearTimeout(sendTimer.current); listener.current?.stop(); stopSpeaking(); }, []);

  const keyRef = useRef(onKey);
  keyRef.current = onKey;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const m: Record<string, Key> = { Enter: "enter", Backspace: "del", "-": "neg", ".": ".", h: "hint", s: "skip" };
      const k = /^\d$/.test(e.key) ? (e.key as Key) : m[e.key];
      if (e.key === "Escape") return void quit();
      if (k) { e.preventDefault(); keyRef.current(k); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  if (phase === "error") return <main className="page bare"><h1>Can't start</h1><p className="error">{err}</p><Link className="btn" to="/">Home</Link></main>;
  if (phase === "summary" && summary) return <SummaryView s={summary} again={req!} />;
  if (!problem) return <main className="page bare"><div className="empty">Loading…</div></main>;

  const p = problem, elapsed = (performance.now() - shownAt.current) / 1000, left = p.target - elapsed;
  const qcls = p.q.length > 26 ? " s" : p.q.length > 14 ? " m" : "";
  const shown = input ? input.replace("-", "−").replace(/^(−?)(\d+)/, (_, s, d) => s + Number(d).toLocaleString("en-US")) : "";
  const r = result, done = phase === "feedback";
  return <main className="play">
    <div className="play-bar">
      <button className="x" onClick={quit} aria-label="End session">✕</button>
      <div className="meta"><span><b>{p.skillName}</b> level {p.level}</span><span>{p.index} of {p.length}</span></div>
    </div>
    <div className="prog"><i style={{ width: `${(Math.max(0, p.index - (done ? 0 : 1)) / p.length) * 100}%` }} /></div>
    <div className="display">
      <div className="tag">{TAGS[p.source] ?? ""}</div>
      <div className={"q" + qcls}>{p.q}</div>
      <div className="note">{p.note}</div>
      <div className={"ans" + (done && r ? (r.right ? " ok" : " bad") : "")} aria-live="polite"><span>{shown}</span><span className="caret" /></div>
      <div className="timer" style={{ transform: `scaleX(${done ? 0 : Math.max(0, Math.min(1, left / p.target))})` }} />
      <div className={"tleft" + (!done && left <= 0 ? " over" : "")}>
        {done ? `target ${fmt(p.target)}s` : left > 0 ? `${Math.ceil(left)}s of ${fmt(p.target)}s` : `${fmt(p.target)}s target passed`}
      </div>
    </div>
    <div className="fb" aria-live="polite">
      {err && <div className="error">{err}</div>}
      {done && r && (r.right
        ? <><span className="res ok">{r.assisted ? "Correct, with a hint" : r.speed === "fast" ? "Fast. Correct" : r.speed === "on-target" ? "Correct" : "Correct, but slow"}</span>{" "}
            in {r.secs}s{r.points > 0 && <> <span className="pts">+{r.points}</span></>}
            {r.approximate && <div>Exact: {fmt(r.answer)}</div>}
            {r.rating && Math.abs(r.rating.after - r.rating.before) >= 0.01 && <div className="small mute">Level {r.rating.before.toFixed(2)} → {r.rating.after.toFixed(2)}</div>}</>
        : <><span className="res bad">{r.skipped ? "Skipped." : "Not quite."}</span> The answer is <b>{fmt(r.answer)}</b>.</>)}
      {(hint || (done && r?.hint)) && <div className="hint">{hint ?? r?.hint}</div>}
    </div>
    {(voice.current.speak || voice.current.listen) && !done && <div className="voicebar">
      <button className="btn" onClick={sayAgain}>Hear again</button>
      {voice.current.listen && <button className="btn" aria-pressed={listening} onClick={() => { if (listening) { listener.current?.stop(); setListening(false); } else { quiet.current = 0; startListening(); } }}>
        {listening ? "Listening. Tap to stop" : "Tap to speak"}</button>}
      <div className="small mute" aria-live="polite">{micMsg || (heard ? `Heard “${heard}”. Say “no” to cancel.` : listening ? "Say your answer. Commands: repeat, hint, skip." : "")}</div>
    </div>}
    <Keypad onKey={onKey} enterLabel={done ? "Next" : "Enter"} hintDisabled={!!hint || done} />
  </main>;
}

function SummaryView({ s, again }: { s: Summary; again: PlayRequest }) {
  const nav = useNavigate();
  const fixed = s.mode === "daily" || s.mode === "challenge";
  const c = s.challenge;
  return <main className="page bare">
    <div className="hero">{s.points}</div>
    <p className="mute" style={{ fontSize: 16 }}>
      points. {s.correct} of {s.answered} correct{s.medianSecs != null ? `, median ${s.medianSecs}s` : ""}.
      {s.cleared ? ` ${s.cleared} review${s.cleared > 1 ? "s" : ""} mastered.` : ""}
    </p>
    {s.debrief && <div className="card insight focus">
      <b>What the coach noticed:</b> {s.debrief.count} of your {s.debrief.total} misses were {s.debrief.label} in {s.debrief.skillName}. {s.debrief.advice}
      {s.debrief.lesson && <div style={{ marginTop: 6 }}><Link to={`/learn/${s.debrief.lesson.key}`}>Strategy: {s.debrief.lesson.title}</Link></div>}
    </div>}
    {s.trial && <div className="card"><b>{s.trial.name} trial, level {s.trial.level}:</b>{" "}
      {s.trial.passed ? `passed with ${s.trial.correct} of ${s.trial.answered}. You're now at level ${s.trial.level}.` : `${s.trial.correct} of ${s.trial.answered}. You need ${s.trial.needed}. Drill it and try again whenever you like.`}</div>}
    {s.lesson && <div className="card"><b>{s.lesson.title}:</b> {s.lesson.learned ? "learned. Nice work." : "not quite yet. Re-read the steps and try again."}</div>}
    {s.goalsDone && s.goalsDone.length > 0 && <div className="card"><b>Goal reached:</b> {s.goalsDone.join(", ")}</div>}
    {s.daily && <div className="card"><b>Daily challenge:</b> {s.daily.rank ? `#${s.daily.rank} of ${s.daily.of} so far.` : "Scored."}</div>}
    {c && <div className="card">
      <b>Duel with {c.opponent.displayName}:</b>{" "}
      {c.outcome === "won" ? `you won, ${c.mine!.score} to ${(c.theirs as any).score}.`
        : c.outcome === "lost" ? `they won, ${(c.theirs as any).score} to ${c.mine!.score}.`
        : c.outcome === "tie" ? "a tie." : "waiting for them to play."}
    </div>}
    <div className="card"><b>This week:</b> {s.week.rank ? `#${s.week.rank} of ${s.week.of} with ${s.week.value} points.` : "no rank yet."}
      {s.streak > 0 && <span className="mute"> {s.streak}-day streak.</span>}</div>
    {s.changes.length > 0 && <div className="card moves">
      {s.changes.map((m) => <div className="mv" key={m.skill}><span>{m.name}</span>
        <span className={m.after > m.before ? "up" : "dn"}>{m.before.toFixed(1)} to {m.after.toFixed(1)}</span></div>)}
    </div>}
    <div className="stack" style={{ marginTop: 20 }}>
      {!fixed && <button className="btn-primary" onClick={() => nav("/play", { state: { ...again }, replace: true })}>Go again</button>}
      {c && <Link className="btn-primary" to="/duels" replace>Back to duels</Link>}
      {s.mode === "drill" && <Link className="btn" to="/grow" replace>Back to Grow</Link>}
      <Link className="btn ink" to="/" replace>Home</Link>
    </div>
  </main>;
}
